/**
 * Scale functions for mapping data values to pixel positions.
 * Provides linear and time scales without external dependencies.
 */

import type { Scale } from "~types/types";

/**
 * Linear interpolation between two values.
 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Inverse linear interpolation.
 */
function inverseLerp(a: number, b: number, value: number): number {
  // Avoid division by zero
  if (a === b) return 0.5;

  return (value - a) / (b - a);
}

/**
 * Clamp value between min and max.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Calculate nice number (rounded to 1, 2, 5, 10 multiples).
 */
function niceNumber(value: number, round: boolean): number {
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const fraction = Math.abs(value) / Math.pow(10, exponent);
  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;

  return niceFraction * Math.pow(10, exponent) * (value < 0 ? -1 : 1);
}

/**
 * Options for creating a linear scale.
 */
export interface LinearScaleOptions {
  /** [min, max] data values */
  domain: [number, number];
  /** [min, max] pixel positions */
  range: [number, number];
  /** Clamp output to range bounds, default: false */
  clamp?: boolean;
  /** Round domain to nice values, default: false */
  nice?: boolean;
}

/**
 * Create a linear scale that maps domain values to pixel range.
 */
export function createLinearScale(options: LinearScaleOptions): Scale {
  const { clamp: shouldClamp = false, nice = false, range } = options;
  let { domain } = options;

  // Apply nice domain rounding if requested
  if (nice) {
    domain = niceDomain(domain);
  }

  const [domainMin, domainMax] = domain;
  const [rangeMin, rangeMax] = range;

  return {
    domain: () => domain,
    range: () => range,

    scale: (value: number): number => {
      // Handle edge case: zero-length domain
      if (domainMin === domainMax) {
        return (rangeMin + rangeMax) / 2;
      }

      const t = inverseLerp(domainMin, domainMax, value);
      const pixel = lerp(rangeMin, rangeMax, t);

      return shouldClamp
        ? clamp(
            pixel,
            Math.min(rangeMin, rangeMax),
            Math.max(rangeMin, rangeMax)
          )
        : pixel;
    },

    invert: (pixel: number): number => {
      // Handle edge case: zero-length range
      if (rangeMin === rangeMax) {
        return (domainMin + domainMax) / 2;
      }

      const t = inverseLerp(rangeMin, rangeMax, pixel);
      const value = lerp(domainMin, domainMax, t);

      if (shouldClamp) {
        return clamp(
          value,
          Math.min(domainMin, domainMax),
          Math.max(domainMin, domainMax)
        );
      }

      return value;
    },
  };
}

/**
 * Options for creating a time scale.
 */
export interface TimeScaleOptions {
  /** [startMs, endMs] timestamps */
  domain: [number, number];
  /** [min, max] pixel positions */
  range: [number, number];
  /** Clamp output to range bounds */
  clamp?: boolean;
}

/**
 * Create a time scale (specialized linear scale for timestamps).
 */
export function createTimeScale(options: TimeScaleOptions): Scale {
  // Time scale is just a linear scale semantically
  return createLinearScale(options);
}

/**
 * Options for tick generation.
 */
export interface TickOptions {
  /** Target number of ticks, default: 5 */
  count?: number;
  /** Minimum pixels between ticks */
  minSpacing?: number;
}

/**
 * Generate tick values for a linear scale.
 */
export function generateLinearTicks(
  scale: Scale,
  options: TickOptions = {}
): number[] {
  const { count = 5 } = options;
  const [domainMin, domainMax] = scale.domain();

  if (domainMin === domainMax) {
    return [domainMin];
  }

  const range = domainMax - domainMin;
  const roughStep = range / (count - 1);
  const niceStep = niceNumber(roughStep, false);

  // Calculate start and end aligned to nice step
  const start = Math.floor(domainMin / niceStep) * niceStep;
  const end = Math.ceil(domainMax / niceStep) * niceStep;

  const ticks: number[] = [];

  for (let tick = start; tick <= end; tick += niceStep) {
    // Round to avoid floating point issues
    const roundedTick = Math.round(tick / niceStep) * niceStep;

    if (roundedTick >= domainMin && roundedTick <= domainMax) {
      ticks.push(roundedTick);
    }
  }

  return ticks.length > 0 ? ticks : [domainMin, domainMax];
}

// Time constants
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
// Approximate
const MONTH = 30 * DAY;
// Approximate
const YEAR = 365 * DAY;

interface TickStrategy {
  interval: number;
  alignTo: (ms: number) => number;
}

function selectMinuteInterval(rangeMs: number, count: number): number {
  const minuteIntervals = [1, 5, 10, 15, 30];
  const targetMinutes = rangeMs / MINUTE / count;

  return minuteIntervals.find((i) => i >= targetMinutes) || 30;
}

function selectHourInterval(rangeMs: number, count: number): number {
  const hourIntervals = [1, 2, 4, 6, 12];
  const targetHours = rangeMs / HOUR / count;

  return hourIntervals.find((i) => i >= targetHours) || 12;
}

function alignToMinuteInterval(ms: number, intervalMs: number): number {
  const d = new Date(ms);

  d.setMinutes(
    Math.floor(d.getMinutes() / (intervalMs / MINUTE)) * (intervalMs / MINUTE),
    0,
    0
  );

  return d.getTime();
}

function alignToHourInterval(ms: number, intervalMs: number): number {
  const d = new Date(ms);

  d.setHours(
    Math.floor(d.getHours() / (intervalMs / HOUR)) * (intervalMs / HOUR),
    0,
    0,
    0
  );

  return d.getTime();
}

function alignToDay(ms: number): number {
  const d = new Date(ms);

  d.setHours(0, 0, 0, 0);

  return d.getTime();
}

function alignToWeek(ms: number): number {
  const d = new Date(ms);

  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());

  return d.getTime();
}

function alignToMonth(ms: number): number {
  const d = new Date(ms);

  d.setDate(1);
  d.setHours(0, 0, 0, 0);

  return d.getTime();
}

function alignToYear(ms: number): number {
  const d = new Date(ms);

  d.setMonth(0, 1);
  d.setHours(0, 0, 0, 0);

  return d.getTime();
}

function selectTickStrategy(rangeMs: number, count: number): TickStrategy {
  if (rangeMs < HOUR) {
    const minutes = selectMinuteInterval(rangeMs, count);
    const intervalMs = minutes * MINUTE;

    return {
      interval: intervalMs,
      alignTo: (ms) => alignToMinuteInterval(ms, intervalMs),
    };
  }

  if (rangeMs < DAY) {
    const hours = selectHourInterval(rangeMs, count);
    const intervalMs = hours * HOUR;

    return {
      interval: intervalMs,
      alignTo: (ms) => alignToHourInterval(ms, intervalMs),
    };
  }

  if (rangeMs < WEEK) {
    return { interval: DAY, alignTo: alignToDay };
  }

  if (rangeMs < MONTH) {
    return { interval: WEEK, alignTo: alignToWeek };
  }

  if (rangeMs < YEAR) {
    return { interval: MONTH, alignTo: alignToMonth };
  }

  return { interval: YEAR, alignTo: alignToYear };
}

/**
 * Generate tick timestamps for a time scale.
 */
export function generateTimeTicks(
  scale: Scale,
  options: TickOptions = {}
): number[] {
  const { count = 5 } = options;
  const [startMs, endMs] = scale.domain();
  const rangeMs = endMs - startMs;

  const { interval, alignTo } = selectTickStrategy(rangeMs, count);

  const ticks: number[] = [];
  let current = alignTo(startMs);

  while (current > startMs) {
    current -= interval;
  }

  while (current <= endMs) {
    if (current >= startMs) {
      ticks.push(current);
    }
    current += interval;
  }

  return ticks.length > 0 ? ticks : [startMs, endMs];
}

/**
 * Round domain to nice values.
 */
export function niceDomain(domain: [number, number]): [number, number] {
  const [min, max] = domain;

  if (min === max) {
    return [min, max];
  }

  const range = max - min;
  const niceRange = niceNumber(range, false);
  const niceMin = Math.floor(min / niceRange) * niceRange;
  const niceMax = Math.ceil(max / niceRange) * niceRange;

  return [niceMin, niceMax];
}

/**
 * Add percentage buffer to domain.
 */
export function domainWithBuffer(
  domain: [number, number],
  bufferRatio = 0.15
): [number, number] {
  const [min, max] = domain;

  if (min === max) {
    // If domain is a single point, create a small range around it
    const buffer = Math.abs(min) * bufferRatio || 1;

    return [min - buffer, max + buffer];
  }

  const range = max - min;
  const buffer = range * bufferRatio;

  return [min - buffer, max + buffer];
}

/**
 * Check if a new value requires rescaling.
 */
export function shouldRescale(
  newValue: number,
  currentDomain: [number, number],
  hysteresisRatio = 0.05
): boolean {
  const [min, max] = currentDomain;
  const range = max - min;
  const threshold = range * hysteresisRatio;

  return newValue < min + threshold || newValue > max - threshold;
}
